import { saveAs } from 'file-saver';
import { DownloadIcon, EyeIcon } from '@heroicons/react/outline';
import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useDDPSubscription } from '@zedoc/ddp-connector';
import { toIntlDateTimeFormat } from '@zedoc/date';
import {
  projectMilestones,
  apiZedocGetCustomPDF,
  apiZedocGetCompletedMilestoneListWithCustomPDF,
} from '../../../common/api/zedoc';
import { default as ProjectMilestoneSelect } from '../../../common/selectors/ProjectMilestone';
import { default as AnswersSheetSelect } from '../../../common/selectors/AnswersSheet';
import { one as oneAnswersSheet } from '../../../common/api/collections/AnswersSheets';
import TranslationsSelect from '../../inputs/TranslationsSelect';
import Spinner from '../../../common/components/Spinner';
import Button from '../../Button';
import CustomPDF from './CustomPDF';
import { callMethod } from '../../../common/utilsClient/ddp/actions';
import { notifyError } from '../../../utils/notify';
import settings from '../../../common/settings';
import { useRum } from '../../../containers/RumContext';

const {
  features: { enableCustomReports },
} = settings.public;

// Number of DownloadCards mounted per animation frame. Each card opens a DDP
// subscription + redux selectors, so mounting all of them in one synchronous
// commit (e.g. 70 at once) blocks the first paint and the Documents tab
// appears frozen for a second or two after the click. See useProgressiveCount.
const PROGRESSIVE_BATCH_SIZE = 10;

// Reveals a long list incrementally: starts with one batch (so the tab paints
// immediately) and mounts another batch on each animation frame until the full
// list is shown. The browser paints between frames, so the click never appears
// frozen — the tab opens first, rows fill in right after.
const useProgressiveCount = (total) => {
  const [count, setCount] = useState(() =>
    Math.min(PROGRESSIVE_BATCH_SIZE, total),
  );
  useEffect(() => {
    if (count >= total) {
      return undefined;
    }
    const raf = requestAnimationFrame(() => {
      setCount((current) => Math.min(current + PROGRESSIVE_BATCH_SIZE, total));
    });
    return () => cancelAnimationFrame(raf);
  }, [count, total]);
  return count;
};

// Renders nothing; exists only to host a single useDDPSubscription per unique
// projectId. Hoisted out of DownloadCard so that 70 rows for the same project
// produce 1 milestone subscription instead of 70.
const ProjectMilestonesSubscriber = ({ projectId }) => {
  useDDPSubscription(projectMilestones.withParams({ projectId }));
  return null;
};

ProjectMilestonesSubscriber.propTypes = {
  projectId: PropTypes.string.isRequired,
};

const DownloadCard = ({ answersSheet, onPreview, onDownload }) => {
  const {
    t,
    i18n: { language },
  } = useTranslation();

  const [answersSheetLanguage, setAnswersSheetLanguage] = useState();

  const handleOnPreview = onPreview ? () => onPreview(answersSheet) : null;
  const handleOnDownload = (audience) =>
    onDownload({
      references: { answersSheetId: answersSheet._id },
      audience,
      language: answersSheetLanguage,
    });

  const answersSheetId = answersSheet._id;
  // Still needed: pulls per-sheet fields (projectId, language) that the parent
  // prop doesn't carry — completedAnswersSheets is assembled from
  // patientRecord.questionnaires and only includes {_id, questionnaireId,
  // milestoneId, completedAt}. See PatientCard.js. This sub is one per row;
  // batching it would require a new server-side byIds API.
  useDDPSubscription(
    answersSheetId && oneAnswersSheet.withParams(answersSheetId),
  );
  const answersSheetDocument = useSelector(
    AnswersSheetSelect.one().whereIdEquals(answersSheetId),
  );
  // The projectMilestones subscription is hoisted to PatientDocuments — see
  // ProjectMilestonesSubscriber above. The selector below reads from Minimongo
  // that the parent's subs populate.
  const milestone = useSelector(
    ProjectMilestoneSelect.one().whereIdEquals(
      answersSheet && answersSheet.milestoneId,
    ),
  );

  return (
    <div className="cluster-4 items-center justify-between flex-wrap">
      <div>
        <p className="font-semibold">
          {milestone && (
            <span>
              {milestone.name}
              {', '}
            </span>
          )}
          {answersSheet.questionnaireId}
        </p>
        {answersSheet.completedAt && (
          <p className="text-sm text-secondary">
            {`${t('completedAt')} ${toIntlDateTimeFormat(
              language,
              answersSheet.completedAt,
              { dateStyle: 'short', timeStyle: 'short' },
            )}`}
          </p>
        )}
      </div>
      <div className="cluster-4 items-center flex-wrap">
        {handleOnPreview && (
          <>
            <Button type="ghost" icon={<EyeIcon />} onClick={handleOnPreview}>
              {t('preview')}
            </Button>
            <div className="w-1 h-6 bg-divider" style={{ width: 1 }} />
          </>
        )}
        {onDownload && (
          <>
            <TranslationsSelect
              type="primary"
              answersSheetId={answersSheetId}
              answersSheetLanguages={
                answersSheetDocument && answersSheetDocument.language
                  ? [answersSheetDocument.language]
                  : []
              }
              onChange={setAnswersSheetLanguage}
              value={answersSheetLanguage}
              valueAsLanguage
            />
            <Button
              type="ghost"
              icon={<DownloadIcon />}
              onClick={() => handleOnDownload('patient')}
            >
              {t('patientPDF')}
            </Button>
            <Button
              icon={<DownloadIcon />}
              onClick={() => handleOnDownload('provider')}
            >
              {t('providerPDF')}
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

// Plain object — assembled at PatientCard.js from
// patientRecord.questionnaires (NOT an AnswersSheet model instance).
// The previous PropTypes.instanceOf(AnswersSheet) was inaccurate and threw a
// "Invalid prop" warning on every render with 70 documents.
DownloadCard.propTypes = {
  answersSheet: PropTypes.shape({
    _id: PropTypes.string.isRequired,
    questionnaireId: PropTypes.string,
    milestoneId: PropTypes.string,
    completedAt: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.instanceOf(Date),
    ]),
  }),
  // Both optional and nullable — parent (PatientDocuments) defaults onPreview
  // to null, and DownloadCard guards both with `onPreview && ...` / `onDownload && ...`.
  onPreview: PropTypes.func,
  onDownload: PropTypes.func,
};

DownloadCard.defaultProps = {
  answersSheet: { _id: '' },
  onPreview: null,
  onDownload: null,
};

const PatientDocuments = ({ recipientId, answersSheets, onPreview }) => {
  const { t } = useTranslation();
  const awsRum = useRum();
  const dispatch = useDispatch();
  const [customPDFs, setCustomPDFs] = useState([]);

  // Read all answersSheet docs the children have hydrated into Minimongo via
  // their per-row oneAnswersSheet subs, then derive the distinct set of
  // projectIds. Drives the hoisted projectMilestones subscriptions — one per
  // project instead of one per row.
  const answersSheetIds = useMemo(
    () => answersSheets.map((s) => s._id),
    [answersSheets],
  );
  const idSet = useMemo(() => new Set(answersSheetIds), [answersSheetIds]);
  const allAnswersSheetDocs = useSelector(
    AnswersSheetSelect.all().satisfying((doc) => idSet.has(doc._id)),
  );
  const uniqueProjectIds = useMemo(() => {
    const ids = new Set();
    allAnswersSheetDocs.forEach((doc) => {
      if (doc && doc.projectId) {
        ids.add(doc.projectId);
      }
    });
    return Array.from(ids);
  }, [allAnswersSheetDocs]);

  // Mount the questionnaire cards a batch at a time so the tab opens instantly
  // instead of freezing while ~70 DownloadCards (each a DDP subscription)
  // mount in one commit.
  const visibleCount = useProgressiveCount(answersSheets.length);
  const visibleAnswersSheets = useMemo(
    () => answersSheets.slice(0, visibleCount),
    [answersSheets, visibleCount],
  );

  const onDownload = ({
    references,
    audience,
    language,
    templateId,
    reportType = 'default',
  }) => {
    const startTime = new Date().getTime();
    dispatch(
      callMethod(apiZedocGetCustomPDF, {
        references,
        audience,
        language,
        reportType,
        templateId,
      }),
    )
      .then((url) => {
        awsRum?.recordEvent('com.zedoc.rum.get_pdf_for_answers_sheet', {
          duration: new Date().getTime() - startTime,
        });

        const blob = new Blob([url], {
          type: 'application/pdf',
        });

        // Generate filename with reference value (always one value)
        const referenceValue = references ? Object.values(references)[0] : '';
        const referenceStr = referenceValue ? `-${referenceValue}` : '';
        const reportTypeStr = reportType === 'default' ? 'default' : 'custom';

        saveAs(blob, `response-${reportTypeStr}${referenceStr}.pdf`);
      })
      .catch(notifyError());
  };

  useEffect(() => {
    if (!recipientId) return;

    if (enableCustomReports) {
      dispatch(
        callMethod(apiZedocGetCompletedMilestoneListWithCustomPDF, {
          recipientId,
        }),
      )
        .then((data) => setCustomPDFs(data || []))
        .catch(notifyError);
    }
  }, [recipientId, dispatch]);

  return (
    <div className="stack-6">
      {/* Hoisted projectMilestones subs — N invisible subscribers, one per
          unique project across the patient's answersSheets. Replaces a per-row
          subscription that would otherwise create N×rows DDP subs. */}
      {uniqueProjectIds.map((projectId) => (
        <ProjectMilestonesSubscriber key={projectId} projectId={projectId} />
      ))}
      {customPDFs?.length > 0 && (
        <div className="stack-4">
          <p className="text-lg font-semibold px-4">{t('customPDFs')}</p>
          <div className="border border-divider rounded-md stack-4 p-4">
            {customPDFs.map((pdf, idx) => (
              <React.Fragment key={pdf.recipientId}>
                {idx !== 0 && <hr className="border-divider" />}
                <CustomPDF
                  title={pdf.pdfReportName}
                  projectName={pdf.projectName}
                  milestoneName={pdf.milestoneName}
                  completedAt={pdf.milestoneCompletedAt}
                  answersSheets={pdf.answersSheets}
                  recipientId={pdf.recipientId}
                  activityId={pdf.activityId}
                  // eslint-disable-next-line no-alert
                  onDownload={() =>
                    onDownload({
                      references: { activityId: pdf.activityId },
                      templateId: pdf.pdfReportId,
                      reportType: pdf.pdfReportType,
                    })
                  }
                />
              </React.Fragment>
            ))}
          </div>
        </div>
      )}
      <div className="stack-4">
        <p className="text-lg font-semibold px-4">{t('questionnaires')}</p>
        <div className="border border-divider rounded-md stack-4 p-4">
          {visibleAnswersSheets.map((answersSheet, idx) => (
            <React.Fragment key={answersSheet._id}>
              {idx !== 0 && <hr className="border-divider" />}
              <DownloadCard
                answersSheet={answersSheet}
                onPreview={onPreview}
                onDownload={onDownload}
              />
            </React.Fragment>
          ))}
          {visibleCount < answersSheets.length && (
            <div className="flex justify-center py-2">
              <Spinner size="18" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

PatientDocuments.propTypes = {
  // Plain objects from PatientCard's completedAnswersSheetsWithDuplicates,
  // NOT AnswersSheet model instances. See DownloadCard.propTypes above.
  answersSheets: PropTypes.arrayOf(
    PropTypes.shape({
      _id: PropTypes.string.isRequired,
      questionnaireId: PropTypes.string,
      milestoneId: PropTypes.string,
      completedAt: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.instanceOf(Date),
      ]),
    }),
  ),
  onPreview: PropTypes.func,
  recipientId: PropTypes.string,
};

PatientDocuments.defaultProps = {
  answersSheets: [],
  onPreview: null,
};

export default PatientDocuments;
